<template>
  <transition name="slideout">
    <div
      v-if="slideoutOpen"
      class="product-slideout-wrapper"
    >
      <div
        class="slideout-overlay"
        data-close
        @click="closeSlideout($event)"
      />
      <div class="bundle-customiser-slideout product-slideout">
        <div class="product-slideout__header flex items-center justify-between">
          <h2 class="h5">
            {{ dropdownTitle }}
          </h2>

          <button
            class="close-btn xs font-semibold flex items-center"
            data-close
            @click="closeSlideout($event)"
          >
            Close
            <CloseIcon />
          </button>
        </div>

        <div
          v-if="hasMultipleSelections"
          class="product-slideout__grid grid grid-cols-2 overflow-auto"
        >
          <div
            v-for="(product, index) in collection.products"
            :key="collectionKey(collection, product)"
            :disabled="!product.available"
            class="product-slideout-qty-item"
            :class="{ 'product-slideout-qty-item--active': !!collection.selectedProducts.find(selectedProduct => selectedProduct.variantId === productVariantId(product) ) }"
          >
            <div class="product-slideout-qty-item__image">
              <img
                v-lazy="product.smallSachetImage || ''"
                :alt="`${product.title}-image`"
                width="52"
              >
            </div>
            <div class="product-slideout-qty-item__content">
              <h3 class="product-slideout-qty-item__title">
                {{ productTitle(product) }}
              </h3>
              <div class="product-slideout-qty-item__quantity quantity-buttons">
                <button
                  class="quantity-buttons__button"
                  @click="decreaseQuantity(product)"
                >
                  <MinusCircleFilled />
                </button>
                <span class="quantity-buttons__amount">
                  {{ productQuantity(product) }}
                </span>
                <button
                  class="quantity-buttons__button"
                  :disabled="maximumProductsReached"
                  @click="increaseQuantity(index)"
                >
                  <PlusCircleFilled />
                </button>
              </div>
            </div>

            <button
              class="product-slideout-qty-item__action-info-cta"
              @click="showSachetPopup(index)"
            >
              <!-- <iconInfo /> -->
            </button>
          </div>
        </div>

        <div
          v-else
          class="product-slideout__grid grid grid-cols-3 overflow-auto text-center"
        >
          <button
            v-for="(product, index) in collection.products"
            :key="collectionKey(collection, product)"
            :disabled="!product.available"
            class="product-slideout__grid-item"
            :class="{ 'product-slideout__grid-item--active': isActiveProduct(product) }"
            @click="selectProduct(index)"
          >
            <div
              class="product-slideout__grid-item-image"
              :class="{ 'product-slideout__grid-item-image--png': product?.gridImage?.includes('.png') }"
            >
              <img
                v-lazy="product.gridImage"
                :alt="product.title"
              >
            </div>
            <h3 class="xs">
              {{ product.title }}
            </h3>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import {mapState, mapActions} from 'pinia'
import {useBundleStore} from '../pinia/BundleStore'

import CloseIcon from '../icons/close-icon'
import PlusCircleFilled from '../icons/plus-circle-filled'
import MinusCircleFilled from '../icons/minus-circle-filled'

import {productPrice} from '../helpers/product'

export default {
  components: {
    CloseIcon,
    PlusCircleFilled,
    MinusCircleFilled
  },
  computed: {
    ...mapState(useBundleStore, [
      'bundle',
      'bundleOptions',
      'isCollectionBundle',
      'slideoutOpen',
      'activeCollectionIndex',
      'selectedProducts'
    ]),
    collection() {
      let key = this.isCollectionBundle ? 0 : this.activeCollectionIndex

      // New metafields version
      if (this?.bundleOptions?.[key]) {
        return this.bundleOptions[key]
      }

      // Old collections version
      if (this.bundle?.collections?.[key]) {
        return this.bundle.collections[key]
      }

      return null
    },
    hasMultipleSelections() {
      return !this.isCollectionBundle && this.collection.maximumQuantity > 1
    },
    selectedProductsArray() {
      return this.selectedProducts[this.activeCollectionIndex]
    },
    maximumProductsReached() {
      return this.collection.selectedProducts.length === this.collection.maximumQuantity
    },
    dropdownTitle() {
      return this.collection.label.replace('[INDEX]', this.activeCollectionIndex + 1).replace('1', '1st').replace('2', '2nd').replace('3', '3rd')
    },
    collectionPriceVaries() {
      const firstProductPrice = this.collection.products[0].price
      let variedPrice = false
      this.collection.products.forEach(product => {
        if (product.price !== firstProductPrice) {
          variedPrice = true
          return
        }
      })
      return variedPrice
    }
  },
  methods: {
    ...mapActions(useBundleStore, [
      'closeSlideout',
      'changeProduct',
      'removeProductFromSelections',
      'addProductToSelections'
    ]),
    collectionKey(collection, product) {
      const key = []

      if (collection?.label) {
        key.push(collection.label)
      } else if (collection?.handle) {
        key.push(collection.handle)
      }

      if (product?.variantId) {
        key.push(product.variantId)
      } else if (product?.variants?.[0]?.id) {
        key.push(product.variants[0].id)
      }

      return key.join('-')
    },
    productVariantId(product) {
      if (product?.variantId) {
        return product.variantId
      }
      if (product?.variants?.[0]?.id) {
        return product.variants[0].id
      }
      return null
    },
    selectProduct(index) {
      if (this.collection.products[index].available) {
        this.changeProduct(index)
      }
      this.closeSlideout()
    },
    decreaseQuantity(product) {
      const variantId = this.productVariantId(product)
      if (this.selectedProductsArray.find(selectedProduct => selectedProduct.variantId === variantId)) {
        this.removeProductFromSelections(variantId)
      }
    },
    increaseQuantity(index) {
      if (!this.maximumProductsReached) {
        this.addProductToSelections(index)
      }
    },
    isActiveProduct(product) {
      if (this.isCollectionBundle) {
        return this.collection.selectedProducts[this.activeCollectionIndex].variantId === this.productVariantId(product)
      } else {
        return !!this.collection.selectedProducts.find(selectedProduct => selectedProduct.variantId === this.productVariantId(product))
      }
    },
    productTitle (product) {
      if (this.getIncrement() > 1) {
        return product.title.toLowerCase().replace('(5 x 10g)', '')
      }
      return product.title.toLowerCase().includes('5 x') ? `5 x ${product.title.toLowerCase().replace('(5 x 10g)', '')}` : product.title.toLowerCase()
    },
    getIncrement () {
      return this.bundle.product.increment || 1
    },
    productQuantity (product) {
      const selectedProduct =  this.selectedProductsArray.find(selectedProduct => selectedProduct.variantId === this.productVariantId(product))
      if (selectedProduct) {
        return selectedProduct.quantity
      }
      return 0
    }
  }
}
</script>
